// Privy hook wrapper — single integration point for the rest of the
// app. Components import `useAuth()`; nobody else touches
// `@privy-io/react-auth` directly.
//
// §5.3 wires this up to `setTokenGetter()` in `lib/api.ts` so the
// axios interceptor can attach the current token. §5.1 ships only
// the type + a stub the layout can render against.

import { useCallback, useEffect } from 'react';
import { usePrivy, useLogin, useLogout, getAccessToken } from '@privy-io/react-auth';
import { setTokenGetter } from './api';
import { env } from './env';
import { useMe } from './use-me';

export type AuthState = {
  ready: boolean;
  authenticated: boolean;
  email: string | undefined;
  privyDid: string | undefined;
  walletAddress: string | undefined;
  role: string | undefined;
  login: () => void;
  logout: () => Promise<void>;
};

// `env.privyAppId` is module-level and constant per session, so the
// branch picked here is stable for the life of the component. React
// only requires hook order to be consistent across renders — same
// branch every render satisfies that. ESLint's react-hooks rule may
// complain; the alternative (always-mount-PrivyProvider) is rejected
// because the Privy SDK throws on a missing/malformed appId.
export function useAuth(): AuthState {
  if (!env.privyAppId) {
    return useStubAuth();
  }
  return usePrivyBackedAuth();
}

const STUB_LOGIN_NOTICE =
  '[auth] no VITE_PRIVY_APP_ID configured — login is a no-op. Use DEV_AUTH_BYPASS for local dev or set the app id.';

function useStubAuth(): AuthState {
  // Wire a no-op token getter so axios doesn't try to call Privy.
  useEffect(() => {
    setTokenGetter(async () => null);
  }, []);

  // Dev-bypass-only: e2e tests inject a role via localStorage
  // (see apps/web/tests/e2e/audience-separation.spec.ts) so RequireAdmin
  // can be exercised without real Privy auth.
  //
  // Default to 'admin' when DEV_AUTH_BYPASS=true and localStorage is unset.
  // Why: the solo founder is the only human in dev-bypass, and silently
  // bouncing /outreach/* and /admin/* to '/' (via RequireAdmin's no-leak
  // redirect) wastes a debugging cycle. E2E tests are unaffected — they all
  // set 'dev-auth-bypass-role' explicitly before navigating. To exercise a
  // non-admin view locally: `localStorage.setItem('dev-auth-bypass-role', 'investor')`.
  const role =
    typeof window !== 'undefined' && env.devAuthBypass
      ? (window.localStorage.getItem('dev-auth-bypass-role') ?? 'admin')
      : undefined;

  return {
    ready: true,
    authenticated: role !== undefined,
    email: undefined,
    privyDid: undefined,
    walletAddress: undefined,
    role: role === null ? undefined : role,
    login: () => console.warn(STUB_LOGIN_NOTICE),
    logout: async () => {},
  };
}

function usePrivyBackedAuth(): AuthState {
  const { ready, authenticated, user } = usePrivy();
  const { login } = useLogin();
  const { logout } = useLogout();

  useEffect(() => {
    if (env.devAuthBypass) return;
    setTokenGetter(async () => {
      try {
        return await getAccessToken();
      } catch {
        return null;
      }
    });
  }, []);

  const me = useMe({ enabled: ready && authenticated });

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  return {
    ready,
    authenticated,
    email: user?.email?.address,
    privyDid: user?.id,
    walletAddress: user?.wallet?.address,
    role: me?.role,
    login,
    logout: handleLogout,
  };
}
